import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShieldAlert,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  User,
  Users,
  Check,
  X,
  MessageSquare,
} from 'lucide-react';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { toast } from 'sonner';
import type { Chamada } from '../../types/models';
import { formatDateBR } from '../../utils/dateUtils';

interface AuditoriaChamadasPanelProps {
  escolaId?: string;
}

export function AuditoriaChamadasPanel({ escolaId: propEscolaId }: AuditoriaChamadasPanelProps) {
  const storeEscolaId = useAuthStore((s) => s.escolaId);
  const escolaId = propEscolaId || storeEscolaId;
  const queryClient = useQueryClient();

  const [modalAprovar, setModalAprovar] = useState<Chamada | null>(null);
  const [modalRejeitar, setModalRejeitar] = useState<Chamada | null>(null);
  const [parecerAprovacao, setParecerAprovacao] = useState('');
  const [motivoRejeicao, setMotivoRejeicao] = useState('');

  // 1. Busca chamadas com statusAuditoria === PENDENTE
  const { data: pendentes = [], isLoading, refetch } = useQuery<Chamada[]>({
    queryKey: ['chamadas-auditoria-pendentes', escolaId],
    queryFn: async () => {
      if (!escolaId) return [];
      const res = await api.get<Chamada[]>(`/escolas/${escolaId}/chamadas/auditoria-pendentes`);
      return res.data;
    },
    enabled: !!escolaId,
    refetchInterval: 15_000,
  });

  // 2. Mutação de Aprovação
  const aprovarMutation = useMutation({
    mutationFn: async ({ chamadaId, parecer }: { chamadaId: string; parecer?: string }) => {
      return api.post(`/escolas/${escolaId}/chamadas/${chamadaId}/aprovar-auditoria`, {
        parecer: parecer?.trim() || undefined,
      });
    },
    onSuccess: () => {
      toast.success('Chamada homologada com sucesso pela Coordenação!');
      setModalAprovar(null);
      setParecerAprovacao('');
      queryClient.invalidateQueries({ queryKey: ['chamadas-auditoria-pendentes', escolaId] });
      queryClient.invalidateQueries({ queryKey: ['chamadas', escolaId] });
    },
    onError: (err: any) => {
      toast.error(`Erro ao aprovar chamada: ${err?.response?.data?.message || err.message}`);
    },
  });

  // 3. Mutação de Rejeição
  const rejeitarMutation = useMutation({
    mutationFn: async ({ chamadaId, motivo }: { chamadaId: string; motivo: string }) => {
      return api.post(`/escolas/${escolaId}/chamadas/${chamadaId}/rejeitar-auditoria`, {
        motivo: motivo.trim(),
      });
    },
    onSuccess: () => {
      toast.success('Chamada rejeitada. Parecer pedagógico enviado ao docente.');
      setModalRejeitar(null);
      setMotivoRejeicao('');
      queryClient.invalidateQueries({ queryKey: ['chamadas-auditoria-pendentes', escolaId] });
      queryClient.invalidateQueries({ queryKey: ['chamadas', escolaId] });
    },
    onError: (err: any) => {
      toast.error(`Erro ao rejeitar chamada: ${err?.response?.data?.message || err.message}`);
    },
  });

  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
        Carregando chamadas pendentes de auditoria...
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header do Painel */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--bg-card, #ffffff)',
          border: '1px solid var(--border-color, #e2e8f0)',
          borderRadius: 14,
          padding: '14px 18px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: pendentes.length > 0 ? '#fef3c7' : '#f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShieldAlert size={20} color={pendentes.length > 0 ? '#d97706' : '#64748b'} />
          </div>
          <div>
            <div style={{ fontSize: '0.92rem', fontWeight: 800, color: 'var(--text-main, #1e293b)' }}>
              Auditoria de Chamadas Fora do Horário
            </div>
            <div style={{ fontSize: '0.74rem', color: '#64748b' }}>
              Homologação de frequências registradas com atraso ou fora da grade prevista
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              padding: '4px 10px',
              borderRadius: 20,
              fontSize: '0.72rem',
              fontWeight: 800,
              background: pendentes.length > 0 ? '#fee2e2' : '#dcfce7',
              color: pendentes.length > 0 ? '#dc2626' : '#15803d',
            }}
          >
            {pendentes.length} {pendentes.length === 1 ? 'pendência' : 'pendências'}
          </span>
          <button
            onClick={() => refetch()}
            style={{
              background: '#f8fafc',
              border: '1px solid #cbd5e1',
              borderRadius: 8,
              padding: '6px 12px',
              fontSize: '0.72rem',
              fontWeight: 700,
              color: '#475569',
              cursor: 'pointer',
            }}
          >
            Atualizar
          </button>
        </div>
      </div>

      {/* Lista de Chamadas Pendentes */}
      {pendentes.length === 0 ? (
        <div
          style={{
            background: 'var(--bg-card, #ffffff)',
            border: '1px dashed #cbd5e1',
            borderRadius: 14,
            padding: '36px 20px',
            textAlign: 'center',
          }}
        >
          <CheckCircle2 size={36} color="#16a34a" style={{ margin: '0 auto 10px' }} />
          <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>
            Nenhuma chamada aguardando auditoria
          </div>
          <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
            Todas as chamadas realizadas fora do horário da grade foram devidamente homologadas.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {pendentes.map((ch) => {
            const dataRef = ch.dataReferencia
              ? formatDateBR(ch.dataReferencia)
              : 'Hoje';

            return (
              <div
                key={ch.id}
                style={{
                  background: 'var(--bg-card, #ffffff)',
                  border: '1px solid #fed7aa',
                  borderRadius: 14,
                  padding: '16px 18px',
                  boxShadow: '0 4px 12px rgba(217, 119, 6, 0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: '#eff6ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#2563eb',
                        fontWeight: 800,
                        fontSize: '0.85rem',
                      }}
                    >
                      <User size={20} />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#1e293b' }}>
                        {ch.professor?.nome || 'Professor'}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        {ch.turma?.nome || 'Turma'} · {ch.materia?.nome || `${ch.numeroAula || 1}ª Aula`} · Data: {dataRef}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {ch.horarioAula && (
                      <span
                        style={{
                          background: '#f1f5f9',
                          color: '#475569',
                          padding: '4px 8px',
                          borderRadius: 6,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <Clock size={12} />
                        Previsto: {ch.horarioAula.horaInicio} às {ch.horarioAula.horaFim}
                      </span>
                    )}
                    <span
                      style={{
                        background: '#fef3c7',
                        color: '#b45309',
                        padding: '4px 8px',
                        borderRadius: 6,
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        border: '1px solid #fde68a',
                      }}
                    >
                      <AlertTriangle size={12} />
                      AUDITORIA PENDENTE
                    </span>
                  </div>
                </div>

                {/* Métricas e Justificativa */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 10,
                    background: '#fffbeb',
                    borderRadius: 10,
                    padding: '10px 14px',
                    border: '1px solid #fef3c7',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#92400e', marginBottom: 2 }}>
                      Frequência Registrada:
                    </div>
                    <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e293b' }}>
                      <span style={{ color: '#16a34a' }}>{ch.totalPresentes ?? 0} Presentes</span> ·{' '}
                      <span style={{ color: '#dc2626' }}>{ch.totalAusentes ?? 0} Ausentes</span> ·{' '}
                      <span style={{ color: '#d97706' }}>{ch.totalAtrasados ?? 0} Atrasados</span>
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#92400e', marginBottom: 2 }}>
                      Justificativa do Docente:
                    </div>
                    <div style={{ fontSize: '0.76rem', color: '#451a03', fontStyle: 'italic' }}>
                      "{ch.justificativaAtraso || ch.observacoes || 'Nenhum motivo informado no momento do registro.'}"
                    </div>
                  </div>
                </div>

                {/* Botões de Ação do Coordenador */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
                  <button
                    onClick={() => {
                      setMotivoRejeicao('');
                      setModalRejeitar(ch);
                    }}
                    style={{
                      background: '#ffffff',
                      color: '#dc2626',
                      border: '1px solid #fca5a5',
                      borderRadius: 8,
                      padding: '8px 14px',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <XCircle size={15} />
                    Rejeitar / Solicitar Esclarecimento
                  </button>

                  <button
                    onClick={() => {
                      setParecerAprovacao('');
                      setModalAprovar(ch);
                    }}
                    style={{
                      background: '#16a34a',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 16px',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      boxShadow: '0 2px 8px rgba(22, 163, 74, 0.2)',
                    }}
                  >
                    <CheckCircle2 size={15} />
                    Homologar / Aprovar Chamada
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal: Confirmar Aprovação */}
      {modalAprovar && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setModalAprovar(null)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: 16,
              padding: '24px 20px',
              maxWidth: 420,
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircle2 size={22} color="#16a34a" />
              </div>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
                  Homologar Chamada
                </div>
                <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                  {modalAprovar.turma?.nome} · {modalAprovar.professor?.nome}
                </div>
              </div>
            </div>

            <p style={{ fontSize: '0.8rem', color: '#475569', lineHeight: 1.5, marginBottom: 14 }}>
              Você confirma a validação pedagógica desta chamada registrada com atraso? O status será marcado como <strong>APROVADA</strong> e os registros de presença serão oficializados no diário.
            </p>

            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: '0.74rem', fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                Parecer da Coordenação (opcional):
              </label>
              <textarea
                value={parecerAprovacao}
                onChange={(e) => setParecerAprovacao(e.target.value)}
                placeholder="Ex: Atraso justificado em virtude de reunião pedagógica extraordinária..."
                rows={2}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: '0.8rem',
                  fontFamily: 'inherit',
                  resize: 'none',
                  outline: 'none',
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setModalAprovar(null)}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button
                onClick={() => aprovarMutation.mutate({ chamadaId: modalAprovar.id, parecer: parecerAprovacao })}
                disabled={aprovarMutation.isPending}
                style={{ background: '#16a34a', color: '#ffffff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Check size={16} />
                {aprovarMutation.isPending ? 'Homologando...' : 'Confirmar Homologação'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Rejeitar / Solicitar Esclarecimento */}
      {modalRejeitar && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setModalRejeitar(null)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: 16,
              padding: '24px 20px',
              maxWidth: 420,
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <XCircle size={22} color="#dc2626" />
              </div>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
                  Rejeitar Chamada
                </div>
                <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                  {modalRejeitar.turma?.nome} · {modalRejeitar.professor?.nome}
                </div>
              </div>
            </div>

            <p style={{ fontSize: '0.8rem', color: '#475569', lineHeight: 1.5, marginBottom: 14 }}>
              Informe o motivo da não homologação. O professor receberá um comunicado interno para retificação do lançamento.
            </p>

            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: '0.74rem', fontWeight: 700, color: '#dc2626', marginBottom: 4 }}>
                Motivo / Parecer Pedagógico (obrigatório):
              </label>
              <textarea
                value={motivoRejeicao}
                onChange={(e) => setMotivoRejeicao(e.target.value)}
                placeholder="Ex: Horário conflitante com outra turma ou grade incorreta..."
                rows={3}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid #fca5a5',
                  fontSize: '0.8rem',
                  fontFamily: 'inherit',
                  resize: 'none',
                  outline: 'none',
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setModalRejeitar(null)}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (!motivoRejeicao.trim()) {
                    toast.error('Informe o motivo da rejeição.');
                    return;
                  }
                  rejeitarMutation.mutate({ chamadaId: modalRejeitar.id, motivo: motivoRejeicao });
                }}
                disabled={rejeitarMutation.isPending}
                style={{ background: '#dc2626', color: '#ffffff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <X size={16} />
                {rejeitarMutation.isPending ? 'Enviando...' : 'Confirmar Rejeição'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
